import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { getProjectTags, type TagSuggestion } from '../api/files'
import type { MemoryItem } from '../api/memory'
import { getProjects } from '../api/projects'
import { type AnswerChunk, parseFilters, searchContext, streamAnswer } from '../api/search'
import SearchFiltersPanel from '../components/SearchFilters'
import type { ProjectResponse, SearchResultItem } from '../types/api'
import { type DocType, toDocTypes } from '../utils/docTypes'
import { formatMatchPercent } from '../utils/format'

export default function SearchPage() {
  const [projects, setProjects] = useState<ProjectResponse[]>([])
  const [selectedProject, setSelectedProject] = useState('')
  const [query, setQuery] = useState('')
  const [limit, setLimit] = useState(10)
  const [results, setResults] = useState<SearchResultItem[]>([])
  const [memories, setMemories] = useState<MemoryItem[]>([])
  const [memoriesOpen, setMemoriesOpen] = useState(true)
  const [documentsOpen, setDocumentsOpen] = useState(true)
  const [isSearching, setIsSearching] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [searchWarning, setSearchWarning] = useState<string | null>(null)
  const [activeResultTab, setActiveResultTab] = useState<'answer' | 'sources'>('sources')

  // Filter state: docTypes (any-of chips) and tags (any-of, autocomplete)
  const [docTypeFilter, setDocTypeFilter] = useState<DocType[]>([])
  const [tagFilter, setTagFilter] = useState<string[]>([])
  const [tagSuggestions, setTagSuggestions] = useState<TagSuggestion[]>([])
  const [isInferring, setIsInferring] = useState(false)
  const [inferredNote, setInferredNote] = useState<string | null>(null)

  useEffect(() => {
    const project = projects.find((p) => p.name === selectedProject)
    if (!project) {
      setTagSuggestions([])
      return
    }
    getProjectTags(project.id)
      .then(setTagSuggestions)
      .catch(() => setTagSuggestions([]))
  }, [selectedProject, projects])

  const handleInferFilters = async () => {
    if (!selectedProject || !query.trim()) return
    setIsInferring(true)
    try {
      const result = await parseFilters(selectedProject, query)
      if (result.docTypes.length > 0 || result.tags.length > 0) {
        setDocTypeFilter(toDocTypes(result.docTypes))
        setTagFilter(result.tags)
      }
      if (result.docTypes.length === 0 && result.tags.length === 0) {
        setInferredNote('No filters inferred')
      } else {
        const parts: string[] = []
        if (result.docTypes.length) parts.push(result.docTypes.join(', '))
        if (result.tags.length) parts.push(`tags: ${result.tags.join(', ')}`)
        setInferredNote(`Inferred: ${parts.join(' · ')}`)
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to infer filters')
    } finally {
      setIsInferring(false)
    }
  }

  // AI Answer state
  const [aiAnswer, setAiAnswer] = useState('')
  const [isGeneratingAnswer, setIsGeneratingAnswer] = useState(false)
  const [answerError, setAnswerError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects()
        setProjects(data)
        if (data.length > 0 && !selectedProject) {
          setSelectedProject(data[0].name)
        }
      } catch {
        toast.error('Failed to load projects')
      }
    }
    fetchProjects()
  }, [selectedProject])

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedProject || !query.trim()) return

    setIsSearching(true)
    setHasSearched(true)
    setSearchWarning(null)
    setAiAnswer('')
    setAnswerError(null)
    setActiveResultTab('sources')

    try {
      const response = await searchContext(selectedProject, query, limit, {
        docTypes: docTypeFilter,
        tags: tagFilter,
      })
      setResults(response.knowledge)
      setMemories(response.memories)
      setMemoriesOpen(true)
      setDocumentsOpen(true)
      setSearchWarning(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Search failed')
      setResults([])
      setMemories([])
      setSearchWarning(null)
    } finally {
      setIsSearching(false)
    }
  }

  const handleGenerateAnswer = async () => {
    if (!selectedProject || !query.trim()) return

    setIsGeneratingAnswer(true)
    setHasSearched(true)
    setResults([])
    setMemories([])
    setSearchWarning(null)
    setAiAnswer('')
    setAnswerError(null)
    setActiveResultTab('answer')

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      await streamAnswer(
        selectedProject,
        query,
        limit,
        (chunk: AnswerChunk) => {
          if (chunk.type === 'sources') {
            setResults(chunk.results ?? [])
            setMemories(chunk.memories ?? [])
            setMemoriesOpen(true)
            setDocumentsOpen(true)
          } else if (chunk.type === 'content' && chunk.content) {
            setAiAnswer((prev) => prev + chunk.content)
          } else if (chunk.type === 'error') {
            setAnswerError(chunk.message ?? 'Unknown error from LLM')
          }
        },
        controller.signal,
        { docTypes: docTypeFilter, tags: tagFilter }
      )
    } catch (error) {
      if (!controller.signal.aborted) {
        setAnswerError(error instanceof Error ? error.message : 'Failed to generate answer')
      }
    } finally {
      setIsGeneratingAnswer(false)
      abortControllerRef.current = null
    }
  }

  const handleStopAnswer = () => {
    abortControllerRef.current?.abort()
    setIsGeneratingAnswer(false)
  }

  const sourceCount = results.length + memories.length

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-harbor-text">Search</h1>
        <p className="text-harbor-text-secondary mt-1">
          Search indexed documents and project memories together
        </p>
      </div>

      {/* Search Form */}
      <form
        onSubmit={handleSearch}
        className="bg-harbor-surface border border-harbor-border rounded-xl p-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Query
            </label>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              required
              className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text placeholder-harbor-text-muted focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              placeholder="What are you looking for?"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Project
            </label>
            <select
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.name}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-harbor-text-secondary mb-1">
              Limit
            </label>
            <input
              type="number"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              min={1}
              max={20}
              className="w-full px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="mt-4">
          <SearchFiltersPanel
            docTypes={docTypeFilter}
            onDocTypesChange={(v) => {
              setDocTypeFilter(v)
              setInferredNote(null)
            }}
            tags={tagFilter}
            onTagsChange={(v) => {
              setTagFilter(v)
              setInferredNote(null)
            }}
            tagSuggestions={tagSuggestions}
            disabled={!query.trim()}
            onInfer={handleInferFilters}
            isInferring={isInferring}
            inferredNote={inferredNote}
          />
        </div>
        <div className="mt-4 flex flex-col-reverse sm:flex-row justify-end gap-2">
          <button
            type="button"
            onClick={handleGenerateAnswer}
            disabled={isGeneratingAnswer || isSearching || !selectedProject || !query.trim()}
            className="h-10 px-6 inline-flex items-center justify-center gap-2 bg-harbor-surface-raised border border-emerald-500/60 text-emerald-300 text-sm font-medium rounded-lg hover:bg-emerald-500/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span aria-hidden="true">✨</span>
            {isGeneratingAnswer ? 'Generating...' : 'Generate Answer'}
          </button>
          <button
            type="submit"
            disabled={isSearching || isGeneratingAnswer || !selectedProject || !query.trim()}
            className="h-10 px-6 inline-flex items-center justify-center bg-emerald-600 text-harbor-text text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {hasSearched && (
        <div className="bg-harbor-surface border border-harbor-border rounded-xl overflow-hidden">
          <div className="px-6 border-b border-harbor-border flex items-center justify-between gap-4">
            <div role="tablist" aria-label="RAG response" className="flex gap-6">
              {(['answer', 'sources'] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  role="tab"
                  aria-selected={activeResultTab === tab}
                  onClick={() => setActiveResultTab(tab)}
                  className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                    activeResultTab === tab
                      ? 'border-emerald-500 text-emerald-300'
                      : 'border-transparent text-harbor-text-secondary hover:text-harbor-text'
                  }`}
                >
                  {tab === 'answer' ? 'Answer' : `Sources (${sourceCount})`}
                </button>
              ))}
            </div>
            {activeResultTab === 'answer' && isGeneratingAnswer && (
              <button
                type="button"
                onClick={handleStopAnswer}
                className="px-3 py-1.5 bg-red-600/80 text-harbor-text text-xs font-medium rounded-lg hover:bg-red-600 transition-colors"
              >
                Stop
              </button>
            )}
          </div>

          {activeResultTab === 'answer' && answerError && (
            <div className="p-4">
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <p className="text-sm text-red-300">
                  <span className="font-medium">Error:</span> {answerError}
                </p>
              </div>
            </div>
          )}

          {activeResultTab === 'answer' && (aiAnswer || isGeneratingAnswer) && (
            <div className="p-6">
              <div className="prose prose-invert max-w-none">
                <div className="text-sm text-harbor-text whitespace-pre-wrap leading-relaxed">
                  {aiAnswer}
                  {isGeneratingAnswer && (
                    <span className="inline-block w-2 h-4 ml-0.5 bg-emerald-400 animate-pulse" />
                  )}
                </div>
              </div>
            </div>
          )}

          {activeResultTab === 'answer' && !aiAnswer && !isGeneratingAnswer && !answerError && (
            <div className="p-8 text-center text-sm text-harbor-text-secondary">
              Generate an answer to summarize the retrieved documents and memories.
            </div>
          )}

          {activeResultTab === 'sources' &&
            (sourceCount === 0 ? (
              <div className="p-12 text-center">
                <div className="text-5xl mb-4">🔍</div>
                <h3 className="text-lg font-semibold text-harbor-text mb-2">No results found</h3>
                {searchWarning ? (
                  <p className="text-sm text-amber-400 max-w-xl mx-auto">{searchWarning}</p>
                ) : (
                  <p className="text-sm text-harbor-text-secondary">
                    Try a different query or project
                  </p>
                )}
              </div>
            ) : (
              <div className="divide-y divide-harbor-border">
                {memories.length > 0 && (
                  <section className="bg-harbor-surface-raised/40">
                    <button
                      type="button"
                      onClick={() => setMemoriesOpen((open) => !open)}
                      aria-expanded={memoriesOpen}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-harbor-surface-raised/60 transition-colors"
                    >
                      <span className="text-xs font-semibold uppercase tracking-wide text-harbor-text-muted">
                        Memories ({memories.length})
                      </span>
                      <span className="text-harbor-text-muted text-sm" aria-hidden="true">
                        {memoriesOpen ? '▾' : '▸'}
                      </span>
                    </button>
                    {memoriesOpen && (
                      <div className="space-y-3 px-4 pb-4">
                        {memories.map((memory) => (
                          <div
                            key={memory.id}
                            className="rounded-lg border border-harbor-border bg-harbor-surface p-4"
                          >
                            <div className="flex items-start justify-between gap-3 mb-2">
                              <span className="px-2 py-0.5 bg-violet-500/15 text-violet-300 text-xs rounded">
                                memory
                              </span>
                              <div className="flex items-center gap-2">
                                {memory.createdByUsername ? (
                                  <span className="text-xs text-harbor-text-muted">
                                    by {memory.createdByUsername}
                                  </span>
                                ) : null}
                                {memory.score != null && (
                                  <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded">
                                    {(memory.score * 100).toFixed(0)}% match
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-harbor-text whitespace-pre-wrap">
                              {memory.memory}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}
                {results.length > 0 && (
                  <section>
                    <button
                      type="button"
                      onClick={() => setDocumentsOpen((open) => !open)}
                      aria-expanded={documentsOpen}
                      className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-harbor-surface-raised/40 transition-colors"
                    >
                      <span className="text-xs font-semibold uppercase tracking-wide text-harbor-text-muted">
                        Documents ({results.length})
                      </span>
                      <span className="text-harbor-text-muted text-sm" aria-hidden="true">
                        {documentsOpen ? '▾' : '▸'}
                      </span>
                    </button>
                    {documentsOpen && (
                      <div className="divide-y divide-harbor-border border-t border-harbor-border">
                        {results.map((result, index) => (
                          <div
                            key={`${result.source}-${result.chunkIndex}-${index}`}
                            className="p-6"
                          >
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-mono text-harbor-text-secondary">
                                  {result.filename}
                                </span>
                                <span className="text-xs text-harbor-text-muted">
                                  chunk #{result.chunkIndex}
                                </span>
                                {result.pageStart != null ? (
                                  <span className="px-1.5 py-0.5 bg-slate-600/30 text-slate-300 text-xs rounded">
                                    {result.pageEnd != null && result.pageEnd !== result.pageStart
                                      ? `pages ${result.pageStart}-${result.pageEnd}`
                                      : `page ${result.pageStart}`}
                                  </span>
                                ) : (
                                  result.pageNum != null && (
                                    <span className="px-1.5 py-0.5 bg-slate-600/30 text-slate-300 text-xs rounded">
                                      p.{result.pageNum}
                                    </span>
                                  )
                                )}
                                {result.sectionPath && (
                                  <span className="px-1.5 py-0.5 bg-purple-500/15 text-purple-300 text-xs rounded truncate max-w-xs">
                                    {result.sectionPath}
                                  </span>
                                )}
                                {result.contentType === 'table' && (
                                  <span className="px-1.5 py-0.5 bg-amber-500/15 text-amber-300 text-xs rounded">
                                    table
                                  </span>
                                )}
                                {result.docType && result.docType !== 'other' && (
                                  <span className="px-2 py-0.5 bg-blue-500/20 text-blue-300 text-xs rounded">
                                    {result.docType}
                                  </span>
                                )}
                                {result.tags?.map((tag) => (
                                  <span
                                    key={tag}
                                    className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 text-xs rounded"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded">
                                {formatMatchPercent(result.score)}% match
                              </span>
                            </div>
                            <div className="bg-harbor-surface-raised rounded-lg p-4">
                              <pre className="text-sm text-harbor-text-secondary whitespace-pre-wrap font-sans">
                                {result.content}
                              </pre>
                            </div>
                            <div className="mt-2 text-xs text-harbor-text-muted">
                              Source: {result.source}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                )}
              </div>
            ))}
        </div>
      )}
    </div>
  )
}
